import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import Dismissible from './common/Dismissible';
import SourceWizard from './sources/SourceWizard';
import {
  getDefaultPollyVoiceForLanguage,
  getPollyLanguageName
} from '../constants/pollyStandardVoices';
import './sources/sourceWizard.css';

const normalizeSourceRecord = (source) => {
  if (!source) {
    return source;
  }

  const sourceLanguage = source.sourceLanguage || source.language || 'en';
  const translationLanguage = source.translationLanguage || source.secondaryLanguage || null;

  // Category healing (T4): regional presets used to be stored as
  // general-na/eu/apac/intl (a second value-space that the category select
  // never offered). Region is now UI-only grouping in the wizard, so heal any
  // stored general-* value down to plain 'general' on load.
  let category = source.category || 'general';
  if (typeof category === 'string' && category.startsWith('general-')) {
    category = 'general';
  }

  const normalized = {
    ...source,
    category,
    sourceLanguage,
    language: sourceLanguage,
    translationLanguage: translationLanguage || null
  };

  if (normalized.translationLanguage) {
    normalized.translationVoiceId =
      source.translationVoiceId || getDefaultPollyVoiceForLanguage(normalized.translationLanguage) || null;
  } else {
    normalized.translationVoiceId = null;
  }

  return normalized;
};

const normalizeSourceList = (items) => (Array.isArray(items) ? items.map((item) => normalizeSourceRecord(item)) : []);

function NewsSourceManager({ user, setActiveTab }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  // Preset (sample) news sources, grouped by region/topic in the wizard's
  // gallery. Regional presets keep their general-* category here only so the
  // wizard can group them; the saved record heals to plain 'general'.
  const sampleSources = [
    // North America
    { name: 'NPR', url: 'https://www.npr.org', category: 'general-na', sourceLanguage: 'en' },
    { name: 'CBC News', url: 'https://www.cbc.ca/news', category: 'general-na', sourceLanguage: 'en' },
    { name: 'NBC News', url: 'https://www.nbcnews.com', category: 'general-na', sourceLanguage: 'en' },
    // Yahoo News URL updated 2026-07-31: news.yahoo.com now 301s to www.yahoo.com/news. Keep in
    // sync with presetSources.js PRESET_SOURCE_URLS.
    { name: 'Yahoo News', url: 'https://www.yahoo.com/news', category: 'general-na', sourceLanguage: 'en' },
    // Europe
    { name: 'BBC News', url: 'https://www.bbc.com/news', category: 'general-eu', sourceLanguage: 'en' },
    { name: 'The Guardian', url: 'https://www.theguardian.com/international', category: 'general-eu', sourceLanguage: 'en' },
    // Sky News dropped 2026-07-31 (owner decision, RSS content too thin - see audit).
    { name: 'France24', url: 'https://www.france24.com/en', category: 'general-eu', sourceLanguage: 'en' },
    { name: 'Deutsche Welle', url: 'https://www.dw.com', category: 'general-eu', sourceLanguage: 'en' },
    { name: 'Euronews', url: 'https://www.euronews.com', category: 'general-eu', sourceLanguage: 'en' },
    { name: 'RTE News', url: 'https://www.rte.ie/news', category: 'general-eu', sourceLanguage: 'en' },
    // Asia Pacific
    { name: 'ABC News Australia', url: 'https://www.abc.net.au/news', category: 'general-apac', sourceLanguage: 'en' },
    { name: 'RNZ', url: 'https://www.rnz.co.nz/news', category: 'general-apac', sourceLanguage: 'en' },
    { name: 'CNA (Channel NewsAsia)', url: 'https://www.channelnewsasia.com', category: 'general-apac', sourceLanguage: 'en' },
    // International
    { name: 'Al Jazeera', url: 'https://www.aljazeera.com', category: 'general-intl', sourceLanguage: 'en' },
    // Technology News
    { name: 'TechCrunch', url: 'https://techcrunch.com', category: 'technology', sourceLanguage: 'en' },
    { name: 'The Verge', url: 'https://www.theverge.com', category: 'technology', sourceLanguage: 'en' },
    { name: 'Ars Technica', url: 'https://arstechnica.com', category: 'technology', sourceLanguage: 'en' },
    // Business News
    { name: 'CNBC', url: 'https://www.cnbc.com', category: 'business', sourceLanguage: 'en' },
    { name: 'Yahoo Finance', url: 'https://finance.yahoo.com/news', category: 'business', sourceLanguage: 'en' },
    // Sports
    // ESPN dropped 2026-07-31: AWS WAF challenge blocks all server-side fetches incl. RSS.
    { name: 'BBC Sport', url: 'https://www.bbc.com/sport', category: 'sports', sourceLanguage: 'en' },
  ];

  // Shared save path used by the wizard's final "Add" step. Keeps the same
  // validation + localStorage-then-backend persistence semantics as before.
  // Throws on validation/persistence failure so the wizard can surface it.
  const saveSource = async ({ name, url, category, sourceLanguage, translationLanguage }) => {
    const trimmedName = (name || '').trim();
    const trimmedUrl = (url || '').trim();

    if (!trimmedName || !trimmedUrl) {
      throw new Error('Please fill in both name and URL');
    }

    // Reuters is blocked (aggressive bot protection / DataDome).
    if (trimmedUrl.toLowerCase().includes('reuters.com') || trimmedName.toLowerCase().includes('reuters')) {
      throw new Error('❌ Reuters is not available due to aggressive bot protection (DataDome). Please use The Guardian or NPR instead.');
    }

    // Basic URL validation
    try {
      // eslint-disable-next-line no-new
      new URL(trimmedUrl);
    } catch (error) {
      throw new Error('Please enter a valid URL (e.g., https://example.com)');
    }

    const secondaryLanguage = translationLanguage || '';
    const translationVoiceId = secondaryLanguage
      ? getDefaultPollyVoiceForLanguage(secondaryLanguage)
      : null;

    if (secondaryLanguage && !translationVoiceId) {
      throw new Error('❌ Selected translation language is not supported by AWS Polly Standard voices.');
    }

    const newSourceRecord = normalizeSourceRecord({
      id: Date.now(),
      name: trimmedName,
      url: trimmedUrl,
      category: category || 'general',
      sourceLanguage: sourceLanguage || 'en',
      language: sourceLanguage || 'en',
      translationLanguage: secondaryLanguage,
      translationVoiceId,
      dateAdded: formatDate(new Date())
    });

    const updatedSources = normalizeSourceList([...sources, newSourceRecord]);

    setLoading(true);
    try {
      localStorage.setItem(`sources_${user.username}`, JSON.stringify(updatedSources));

      try {
        await apiService.saveUserSources(user.username, updatedSources);
        console.log('Sources saved to database');
      } catch (dbError) {
        console.warn('Could not save to database, using local storage:', dbError);
      }

      setSources(updatedSources);
      setMessage('✅ News source added successfully!');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSource = async (id) => {
    try {
      setLoading(true);

      // Remove from database. Tolerate a backend miss (e.g. a source that only
      // existed in localStorage and was never persisted) so the UI can still
      // drop it locally instead of getting stuck with an undeletable entry.
      try {
        await apiService.removeNewsSource(user.username, id);
      } catch (apiError) {
        console.warn('Backend remove failed, removing locally anyway:', apiError);
      }

      // Update local state and localStorage
      const updatedSources = sources.filter(source => source.id !== id);
      setSources(updatedSources);
      localStorage.setItem(`sources_${user.username}`, JSON.stringify(updatedSources));

      setMessage('✅ Source removed successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error removing source:', error);
      setMessage('❌ Error removing source. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Load sources when component mounts
  useEffect(() => {
    const loadSources = async () => {
      const savedSources = localStorage.getItem(`sources_${user.username}`);
      const localSources = savedSources ? normalizeSourceList(JSON.parse(savedSources)) : [];

      try {
        // Try to load from backend first
        const backendSources = await apiService.getUserSources(user.username);
        if (backendSources.length > 0) {
          const normalized = normalizeSourceList(backendSources);
          setSources(normalized);
          localStorage.setItem(`sources_${user.username}`, JSON.stringify(normalized));
          return;
        }

        // Backend is empty but we have local sources — these were saved before the
        // API was authenticated and never persisted. Migrate them up to the backend
        // so schedules and deletes (which read from the backend) can see them.
        if (localSources.length > 0) {
          try {
            await apiService.saveUserSources(user.username, localSources);
            console.log('Migrated local-only news sources to backend');
          } catch (migrateError) {
            console.warn('Could not migrate local sources to backend:', migrateError);
          }
          setSources(localSources);
          return;
        }
      } catch (error) {
        console.warn('Could not load from database, using local storage:', error);
      }

      // Fallback to local storage
      if (localSources.length > 0) {
        setSources(localSources);
      }
    };

    loadSources();
  }, [user.username]);

  return (
    <div className="component-card">
      <h2>📰 News Source Manager</h2>

      {message && (
        <div className={message.includes('✅') ? 'success' : 'error'}>
          {message}
        </div>
      )}

      {/* Primary entry point — everything else happens in the wizard. */}
      <div className="sources-toolbar">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setWizardOpen(true)}
          disabled={loading}
        >
          ➕ Add news source
        </button>
      </div>

      {/* Current Sources List */}
      <div>
        <h3 className="sources-list-heading">Your News Sources ({sources.length})</h3>

        {sources.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon" aria-hidden="true">📝</div>
            <h3 className="empty-state__title">No news sources added yet</h3>
            <p className="empty-state__text">Click “➕ Add news source” to get started with your personalized podcast!</p>
          </div>
        ) : (
          <ul className="source-list">
            {sources.map((source) => (
              <li key={source.id} className="source-item">
                <div className="source-item__body">
                  <h4 className="source-item__name">{source.name}</h4>
                  <p className="source-item__url">{source.url}</p>
                  <div className="source-item__meta">
                    <span>📂 {source.category}</span>
                    <span>🌐 Source: {source.sourceLanguage || source.language}</span>
                    <span>
                      🌍 Secondary: {source.translationLanguage
                        ? `${getPollyLanguageName(source.translationLanguage)} · Voice ${source.translationVoiceId}`
                        : 'None'}
                    </span>
                    <span>📅 Added: {source.dateAdded}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveSource(source.id)}
                  className="btn btn-danger source-item__remove"
                >
                  🗑️ Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {sources.length > 0 && (
        <Dismissible id="sources-next-step" title="Next step" userSub={user?.username}>
          <div className="sources-cta">
            <p className="sources-cta__text">
              🎉 Great! You have {sources.length} news source{sources.length !== 1 ? 's' : ''} configured.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setActiveTab && setActiveTab('schedules')}
            >
              📅 Create Schedules from These Sources
            </button>
          </div>
        </Dismissible>
      )}

      {wizardOpen && (
        <SourceWizard
          presets={sampleSources}
          existingSources={sources}
          onSave={saveSource}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}

export default NewsSourceManager;
